import axios from 'axios'
import React, { useState, useCallback, useEffect, useReducer } from 'react'
import { ToastContainer, toast } from 'react-toastify'
import {
  getCurrentAccount,
  refreshCurrentAccountWrapperInFunctionalComponent
} from '../services/session/sessionState'

export const CartContext = React.createContext([null])

const CartProvider = (props) => {
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(
    !localStorage.getItem('checkoutSelectedCustomerIds')
      ? [getCurrentAccount().id]
      : JSON.parse(localStorage.getItem('checkoutSelectedCustomerIds'))
  )
  const [cart, setCart] = useState([])
  const [loading, setLoading] = useState(false)

  const [, forceUpdate] = useReducer((x) => x + 1, 0)
  useEffect(() => {
    refreshCurrentAccountWrapperInFunctionalComponent(forceUpdate, props)
    const interval = setInterval(() => {
      const currentAccount = getCurrentAccount()

      if (!!currentAccount && !!currentAccount.id) {
        getItemsFromApi([getCurrentAccount().id])
        clearInterval(interval)
      }
    }, 1000)
  }, [])

  const resetCart = useCallback(async () => {
    setLoading(true)
    localStorage.removeItem('checkoutSelectedCustomerIds')
    setSelectedCustomerIds([getCurrentAccount().id])
    setCart([])
    setLoading(false)
  })

  const addToCart = useCallback(async (items) => {
    const currentAccount = getCurrentAccount()

    for (let i = 0; i < items.length; i++) {
      const postData = {
        articleCode: items[i].articleCode,
        units: parseInt(items[i].units, 10),
        customerId: currentAccount.id
      }

      try {
        const data = await axios.post(process.env.REACT_APP_API_URL + '/api/ShoppingCart', postData)
        items[i] = { ...items[i], ...data.data.data }

        if (data.data.isError) {
          toast.error('Error !', {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 5000
          })
          console.error(data.data.errorMessage)
        }
      } catch (error) {
        console.error(error)
      }

      i++
    }

    const newItems = [...cart, ...items]
    setCart(newItems)
  })

  const updateCart = useCallback(async (items) => {
    const currentAccount = getCurrentAccount()
    const newItems = [...cart]
    const currentItem = newItems.filter((item) => item.articleCode === items[0].articleCode)[0]
    if (!currentItem) return addToCart(items)
    currentItem.units = parseInt(items[0].units, 10)
    setCart(newItems)
    const postData = {
      idCartItem: currentItem.id,
      units: currentItem.units,
      customerId: currentAccount.id
    }

    try {
      const data = await axios.put(process.env.REACT_APP_API_URL + '/api/ShoppingCart', postData)

      if (data.data.isError) {
        toast.error('Error !', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000
        })
        console.error(data.data.errorMessage)
      } else {
        await getItemsFromApi(selectedCustomerIds)
      }
    } catch (error) {
      console.error(error)
    }
  })

  const removeFromCart = useCallback(async (item) => {
    const currentAccount = getCurrentAccount()
    let cartItemId = null
    const newItems = cart.filter((currentItem) => {
      if (currentItem.articleCode === item.articleCode) cartItemId = currentItem.id
      return currentItem.articleCode !== item.articleCode
    })
    setCart(newItems)

    try {
      const data = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/ShoppingCart/${currentAccount.id}/${cartItemId}`
      )

      if (data.data.isError) {
        toast.error('Error !', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000
        })
        console.error(data.data.errorMessage)
      } else {
        await getItemsFromApi(selectedCustomerIds)
      }
    } catch (error) {
      console.error(error)
    }
  })

  const refreshCart = useCallback(async () => {
    setCart([])

    try {
      await getItemsFromApi(selectedCustomerIds)
    } catch (error) {
      console.error(error)
    }
  })
  const cleanCart = useCallback(async () => {
    const currentAccount = getCurrentAccount()
    setCart([])

    try {
      const data = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/ShoppingCart/RemoveAllCartItems/${currentAccount.id}`
      )

      if (data.data.isError) {
        toast.error('Error !', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000
        })
        console.error(data.data.errorMessage)
      } else {
        await getItemsFromApi(selectedCustomerIds)
      }
    } catch (error) {
      console.error(error)
    }
  })

  const getItemsFromApi = useCallback(async (customerIds) => {
    if (loading) return

    const currentAccount = getCurrentAccount()
    setLoading(true)

    try {
      const data = await axios.post(process.env.REACT_APP_API_URL + '/api/ShoppingCart/Get', {
        customerId: currentAccount.id,
        selectedCustomerIds: customerIds ? customerIds : selectedCustomerIds,
        includeImages: false
      })

      if (!data.data.isError) {
        setLoading(false)
        setCart(data.data.data.items)
        setSelectedCustomerIds(customerIds ? customerIds : selectedCustomerIds)
      } else {
        console.error(data.data.errorMessage)
        setLoading(false)
      }

      return data
    } catch (error) {
      console.error(error)
      setLoading(false)
    }
  })

  const addToCartWithFastrack = useCallback(async (code, units, isArticleCode, customerIds) => {
    const currentAccount = getCurrentAccount()

    try {
      const data = await axios.post(process.env.REACT_APP_API_URL + '/api/ShoppingCart/Fasttrack', {
        customerId: currentAccount.id,
        code,
        units,
        isArticleCode
      })

      if (!data.data.isError) {
        toast.success('¡Artículo añadido!', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000
        })
        await getItemsFromApi(customerIds)
      } else {
        toast.error('¡Código de artículo no encontrado!', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000
        })
        console.error(data.data.errorMessage)
      }
    } catch (error) {
      console.error(error)
    }
  })

  const addToCartWithEanCode = useCallback(async (code, units) => {
    const currentAccount = getCurrentAccount()

    try {
      const data = await axios.post(process.env.REACT_APP_API_URL + '/api/ShoppingCart/EanCode', {
        customerId: currentAccount.id,
        code,
        units
      })

      if (!data.data.isError) {
        toast.success('¡Artículo añadido!', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000
        })
        await getItemsFromApi()
      } else {
        toast.error('¡Código de artículo no encontrado!', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000
        })
        console.error(data.data.errorMessage)
      }
    } catch (error) {
      console.error(error)
    }
  })

  const addSuggestedProducts = useCallback(async (isSuggested) => {
    const currentAccount = getCurrentAccount()
    setLoading(true)

    try {
      const data = await axios.post(
        process.env.REACT_APP_API_URL + '/api/ShoppingCart/AddSuggested',
        {
          customerId: currentAccount.id,
          isSuggested: isSuggested
        }
      )

      if (!data.data.isError) {
        toast.success('¡Artículos añadidos!', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000
        })
        await getItemsFromApi(selectedCustomerIds)
      } else {
        toast.error('¡Error!', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000
        })
        console.error(data.data.errorMessage)
      }
    } catch (error) {
      console.error(error)
    }
  })

  return (
    <CartContext.Provider
      value={{
        cart: cart,
        loading: loading,
        resetCart: resetCart,
        addToCart: addToCart,
        updateCart: updateCart,
        removeFromCart: removeFromCart,
        loadCart: getItemsFromApi,
        cleanCart: cleanCart,
        refreshCart: refreshCart,
        addToCartWithFastrack: addToCartWithFastrack,
        addToCartWithEanCode: addToCartWithEanCode,
        addSuggestedProducts: addSuggestedProducts
      }}
    >
      <ToastContainer />
      {props.children}
    </CartContext.Provider>
  )
}

export const withCartContext = (Component) => (props) => (
  <CartContext.Consumer>
    {(context) => <Component cartContext={context} {...props} />}
  </CartContext.Consumer>
)

export default CartProvider
